import React, { useEffect, useMemo, useState } from 'react';
import { DemeterCommodity } from '../../../Generated/Raven-Demeter';
import { useApplicationSelector } from '../../../Redux/ReduxStore';
import { selectMarketPricesBySymbol } from '../../../Redux/Slices/MarketPricesSlice';
import { selectStoreId } from '../../../Redux/Slices/SystemSlice';
import useSymbolsApi from '../../Apis/Hooks/useSymbolsApiHook';
import useSearchParameters from '../../Components/Navigation/Hooks/useSearchParametersHook';
import formattingService from '../../Services/Formatting/FormattingService';
import useLanguage from '../../Services/Language/useLanguageHook';
import lightstreamerMarketPricesService from '../../Services/MarketPrices/LightstreamerMarketPricesService';
import styles from './MarketIntelligenceReports.module.scss';
import MarketIntelligenceReportsChart from './MarketIntelligenceReportsChart';
import { CurrencyConversionRates, MarketPriceSymbolContractCompositeModel } from './MarketIntelligenceReportsDefinitions';
import MarketIntelligenceArbitrageTable from './MarketIntelligenceReportsTables/MarketIntelligenceArbitrageTable';
import MarketIntelligenceMarketPricesTable from './MarketIntelligenceReportsTables/MarketIntelligenceMarketPricesTable';
import MarketIntelligenceSpreadTable from './MarketIntelligenceReportsTables/MarketIntelligenceSpreadTable';

// Defaults and keys.
const defaultCommodity = DemeterCommodity.Cocoa;
const currenciesToWatch = ['MYR=', 'GBP=', 'EUR='];

const MarketIntelligenceReportsPage: React.FC = () => {
    // Application hooks.
    const [searchParameters, setSearchParameters] = useSearchParameters();
    const [translations] = useLanguage();
    const storeId = useApplicationSelector(selectStoreId);

    // Chart hooks.
    const marketPricesBySymbol = useApplicationSelector(selectMarketPricesBySymbol);
    const [currencyConversions, setCurrencyConversions] = useState<CurrencyConversionRates>({
        USD: 1,
        USC: 100,
        GBP: 1,
        EUR: 1,
        MYR: 1,
    });

    // Api hooks.
    const symbols = useSymbolsApi();

    // Use user provided requests first. If none provided, use defaults.
    const reutersInstrumentCodes = useMemo(() => {
        const searchParametersSuppliedCodes = searchParameters?.reutersInstrumentCodes?.split('_') ?? [];
        const searchParametersPrefixArray = searchParameters?.reutersInstrumentCodePrefixes?.split('_');
        const codesFromPrefixes = searchParameters?.reutersInstrumentCodePrefixes
            ? searchParametersPrefixArray.map((x) => symbols?.find((y) => y.reutersInstrumentCodePrefix === x)!.symbolContracts[0].reutersInstrumentCode!)
            : [];

        // Filter duplicates.
        const filteredCodesFromPrefixes = codesFromPrefixes.filter(
            (x, index) => !searchParametersSuppliedCodes.some((y) => y.startsWith(searchParametersPrefixArray[index])),
        );
        const symbolsLoading = !symbols;
        const noCodes = searchParametersSuppliedCodes.length === 0 && filteredCodesFromPrefixes.length === 0;

        if (symbolsLoading || noCodes) {
            return [];
        }

        if (searchParametersSuppliedCodes.length === 0) {
            return filteredCodesFromPrefixes;
        }

        if (filteredCodesFromPrefixes.length === 0) {
            return searchParametersSuppliedCodes;
        }

        return [...searchParametersSuppliedCodes, ...filteredCodesFromPrefixes];
    }, [symbols, searchParameters]);

    useEffect(() => {
        if (!symbols || searchParameters?.reutersInstrumentCodes || searchParameters?.reutersInstrumentCodePrefixes) {
            return;
        }

        setSearchParameters({ reutersInstrumentCodes: symbols?.find((x) => x.commodity === defaultCommodity)?.symbolContracts[0].reutersInstrumentCode! });
    }, [symbols, searchParameters]);

    const arbitrageSymbolPrefixes = useMemo(() => {
        if (!symbols || reutersInstrumentCodes.length === 0) {
            return [];
        }

        const prefixes: string[] = [];

        reutersInstrumentCodes.forEach((x) => {
            if (x.startsWith('BO')) {
                prefixes.push('CPO');
            } else if (x.startsWith('CPO')) {
                prefixes.push('BO');
            } else if (x.startsWith('CC')) {
                prefixes.push('LCC');
            } else if (x.startsWith('LCC')) {
                prefixes.push('CC');
            } else if (x.startsWith('LRC')) {
                prefixes.push('KC');
            } else {
                prefixes.push('LRC');
            }
        });

        return prefixes;
    }, [symbols, reutersInstrumentCodes]);

    // Tell the lightstreamer service to get new data if the commodity changes.
    useEffect(() => {
        const symbolModels = symbols?.filter((x) => x.symbolContracts.some((y) => reutersInstrumentCodes.includes(y.reutersInstrumentCode)));

        if (!symbolModels?.some((x) => x.symbolContracts)) {
            return;
        }

        const codes = symbolModels.map((x) => x.symbolContracts.map((y) => y.reutersInstrumentCode));

        lightstreamerMarketPricesService.start(storeId, [...currenciesToWatch, ...codes.flat()]);
    }, [reutersInstrumentCodes]);

    // Stop the lightstreamer service when we leave this component.
    useEffect(
        () => () => {
            lightstreamerMarketPricesService.stop(storeId);
        },
        [],
    );

    const compositeSymbolModels = useMemo(() => {
        if (!marketPricesBySymbol || !symbols) {
            return [];
        }

        const getCurrencyRate = (symbol: keyof typeof marketPricesBySymbol) => {
            if (marketPricesBySymbol[symbol] && marketPricesBySymbol[symbol].askPrice && marketPricesBySymbol[symbol].bidPrice) {
                return (marketPricesBySymbol[symbol].askPrice! + marketPricesBySymbol[symbol].bidPrice!) / 2;
            }

            return 1;
        };

        setCurrencyConversions({
            ...currencyConversions,
            EUR: getCurrencyRate('EUR='),
            GBP: getCurrencyRate('GBP='),
            MYR: getCurrencyRate('MYR='), // Possible future use.
        });

        return symbols
            ?.filter((x) => x.symbolContracts.some((y) => reutersInstrumentCodes.includes(y.reutersInstrumentCode)))
            .map((y) =>
                y?.symbolContracts.map((x) => {
                    const currentMarketPrice = marketPricesBySymbol[x.reutersInstrumentCode];

                    return {
                        ...currentMarketPrice,
                        commodityDisplayName: y.displayName,
                        reutersInstrumentCodePrefix: y.reutersInstrumentCodePrefix,
                        ...x,
                        unitOfMeasure: y.unitOfMeasure,
                        currency: y.currency,
                    };
                }),
            );
    }, [marketPricesBySymbol, reutersInstrumentCodes, symbols]);

    const loading = !symbols || reutersInstrumentCodes.length === 0;

    return (
        <div data-testid="MarketIntelligenceReportsPage" className={styles.market_intelligence_reports_page}>
            {reutersInstrumentCodes.map((x, index) => {
                const symbolModel = symbols!.find((y) => y.symbolContracts.some((z) => z.reutersInstrumentCode === x));
                const commodityDisplayName = symbolModel?.displayName ?? '';
                const currentCompositeSymbols = compositeSymbolModels.find((y) =>
                    y.find((z) => z.reutersInstrumentCode === x),
                ) as MarketPriceSymbolContractCompositeModel[];
                const arbitrageSymbolContracts = symbols!.find((y) => arbitrageSymbolPrefixes[index] === y.reutersInstrumentCodePrefix)?.symbolContracts;
                const currentCompositeSymbol = currentCompositeSymbols?.find((y) => y.reutersInstrumentCode === x);
                const tablesLoading = !currentCompositeSymbols;

                return (
                    <span key={x}>
                        <div className={styles.market_intelligence_reports_spacing_wrapper}>
                            <MarketIntelligenceReportsChart reutersInstrumentCode={x} />
                        </div>

                        <div className={styles.market_intelligence_reports_spacing_wrapper}>
                            <MarketIntelligenceMarketPricesTable
                                symbolContracts={currentCompositeSymbols}
                                loading={tablesLoading}
                                testId={`MarketIntelligenceReportsPricesTable_${x}`}
                            />
                        </div>

                        {!tablesLoading && (
                            <>
                                <div className={styles.market_intelligence_reports_spacing_wrapper_headline}>
                                    <p className={styles.market_intelligence_reports_date}>
                                        {formattingService.toDayOfWeekWithDayMonthYear(currentCompositeSymbol?.latestPriceAsOfDate)}
                                    </p>
                                    <p className={styles.market_intelligence_reports_number_header}>
                                        {formattingService.formatNumber(currentCompositeSymbol?.openInterest)}
                                    </p>
                                    <p className={styles.home_page_general_text_header}>
                                        {`${currentCompositeSymbol?.openInterestNetChange} ${translations.marketIntelligenceReports.headers.openInterest}`}
                                    </p>
                                </div>
                                <div className={styles.market_intelligence_reports_spacing_wrapper_headline}>
                                    <p className={styles.market_intelligence_reports_text_header}>{translations.words.volume}: </p>
                                    <p className={styles.market_intelligence_reports_number_header}>
                                        {formattingService.formatNumber(currentCompositeSymbol?.volume)}
                                    </p>
                                </div>
                            </>
                        )}

                        <div className={styles.market_intelligence_reports_spacing_wrapper}>
                            <MarketIntelligenceSpreadTable
                                symbolContracts={currentCompositeSymbols}
                                loading={tablesLoading}
                                testId={`MarketIntelligenceReportsSpreadTable_${x}`}
                            />
                        </div>

                        {(tablesLoading || (currentCompositeSymbols && arbitrageSymbolContracts)) && (
                            <div className={styles.market_intelligence_reports_spacing_wrapper}>
                                <MarketIntelligenceArbitrageTable
                                    symbolContracts={currentCompositeSymbols}
                                    arbitrageSymbolContracts={arbitrageSymbolContracts!}
                                    currencyConversionRates={currencyConversions}
                                    loading={tablesLoading}
                                    testId={`MarketIntelligenceReportsArbitrageTable_${x}`}
                                />
                            </div>
                        )}
                    </span>
                );
            })}
        </div>
    );
};

export default MarketIntelligenceReportsPage;
